import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute.tsx";
import { RequireRole } from "@/components/RequireRole.tsx";
import { RequireTerminal } from "@/components/RequireTerminal.tsx";
import { ADMIN_ROLES } from "@/constants/roles.ts";
import { IS_ONLINE_EDITION } from "@/lib/edition.ts";
import { setupApi } from "@/lib/setup-api.ts";
import { LoginPage } from "@/pages/LoginPage.tsx";
import { OpenCashPage } from "@/pages/OpenCashPage.tsx";
import { POSPage } from "@/pages/POSPage.tsx";
import { SaleSuccessPage } from "@/pages/SaleSuccessPage.tsx";
import { SelectTerminalPage } from "@/pages/SelectTerminalPage.tsx";

// Lazy: el bundle del cajero no debe traer la consola admin.
const AdminLayout = lazy(() =>
  import("@/pages/admin/AdminLayout.tsx").then((m) => ({ default: m.AdminLayout })),
);
const ProductsPage = lazy(() =>
  import("@/pages/admin/ProductsPage.tsx").then((m) => ({ default: m.ProductsPage })),
);
const CategoriesPage = lazy(() =>
  import("@/pages/admin/CategoriesPage.tsx").then((m) => ({ default: m.CategoriesPage })),
);
const BrandsPage = lazy(() =>
  import("@/pages/admin/BrandsPage.tsx").then((m) => ({ default: m.BrandsPage })),
);
const UnitsPage = lazy(() =>
  import("@/pages/admin/UnitsPage.tsx").then((m) => ({ default: m.UnitsPage })),
);
const BranchesPage = lazy(() =>
  import("@/pages/admin/BranchesPage.tsx").then((m) => ({ default: m.BranchesPage })),
);
const SeriesPage = lazy(() =>
  import("@/pages/admin/SeriesPage.tsx").then((m) => ({ default: m.SeriesPage })),
);
const TerminalsPage = lazy(() =>
  import("@/pages/admin/TerminalsPage.tsx").then((m) => ({ default: m.TerminalsPage })),
);
const InventoryPage = lazy(() =>
  import("@/pages/admin/InventoryPage.tsx").then((m) => ({ default: m.InventoryPage })),
);
const SetupPage = lazy(() =>
  import("@/pages/admin/SetupPage.tsx").then((m) => ({ default: m.SetupPage })),
);
const AdminHome = lazy(() =>
  import("@/pages/admin/SetupPage.tsx").then((m) => ({ default: m.AdminHome })),
);
const ReportsPage = lazy(() =>
  import("@/pages/admin/ReportsPage.tsx").then((m) => ({ default: m.ReportsPage })),
);
const DocumentsPage = lazy(() =>
  import("@/pages/admin/DocumentsPage.tsx").then((m) => ({ default: m.DocumentsPage })),
);
const NodesPage = lazy(() =>
  import("@/pages/admin/NodesPage.tsx").then((m) => ({ default: m.NodesPage })),
);
const SyncPage = lazy(() =>
  import("@/pages/admin/SyncPage.tsx").then((m) => ({ default: m.SyncPage })),
);
const UsersPage = lazy(() =>
  import("@/pages/admin/UsersPage.tsx").then((m) => ({ default: m.UsersPage })),
);
const CustomersPage = lazy(() =>
  import("@/pages/admin/CustomersPage.tsx").then((m) => ({ default: m.CustomersPage })),
);

function AdminFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-slate-500">
      Cargando consola admin…
    </div>
  );
}

export function App() {
  useEffect(() => {
    setupApi();
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* POS — cajero. Selector de terminal entre login y POS. */}
      <Route
        path="/select-terminal"
        element={
          <ProtectedRoute>
            <SelectTerminalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/open-caja"
        element={
          <ProtectedRoute>
            <RequireTerminal>
              <OpenCashPage />
            </RequireTerminal>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sale/:saleId/success"
        element={
          <ProtectedRoute>
            <RequireTerminal>
              <SaleSuccessPage />
            </RequireTerminal>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RequireTerminal>
              <POSPage />
            </RequireTerminal>
          </ProtectedRoute>
        }
      />

      {/* Admin — gated por rol */}
      <Route
        path="/admin"
        element={
          <RequireRole allow={ADMIN_ROLES}>
            <Suspense fallback={<AdminFallback />}>
              <AdminLayout />
            </Suspense>
          </RequireRole>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<AdminFallback />}>
              <AdminHome />
            </Suspense>
          }
        />
        <Route
          path="setup"
          element={
            <Suspense fallback={<AdminFallback />}>
              <SetupPage />
            </Suspense>
          }
        />
        <Route
          path="products"
          element={
            <Suspense fallback={<AdminFallback />}>
              <ProductsPage />
            </Suspense>
          }
        />
        <Route
          path="inventory"
          element={
            <Suspense fallback={<AdminFallback />}>
              <InventoryPage />
            </Suspense>
          }
        />
        <Route
          path="reports"
          element={
            <Suspense fallback={<AdminFallback />}>
              <ReportsPage />
            </Suspense>
          }
        />
        <Route
          path="documents"
          element={
            <Suspense fallback={<AdminFallback />}>
              <DocumentsPage />
            </Suspense>
          }
        />
        {IS_ONLINE_EDITION && (
          <Route
            path="nodes"
            element={
              <Suspense fallback={<AdminFallback />}>
                <NodesPage />
              </Suspense>
            }
          />
        )}
        {!IS_ONLINE_EDITION && (
          <Route
            path="sync"
            element={
              <Suspense fallback={<AdminFallback />}>
                <SyncPage />
              </Suspense>
            }
          />
        )}
        <Route
          path="categories"
          element={
            <Suspense fallback={<AdminFallback />}>
              <CategoriesPage />
            </Suspense>
          }
        />
        <Route
          path="brands"
          element={
            <Suspense fallback={<AdminFallback />}>
              <BrandsPage />
            </Suspense>
          }
        />
        <Route
          path="units"
          element={
            <Suspense fallback={<AdminFallback />}>
              <UnitsPage />
            </Suspense>
          }
        />
        <Route
          path="branches"
          element={
            <Suspense fallback={<AdminFallback />}>
              <BranchesPage />
            </Suspense>
          }
        />
        <Route
          path="series"
          element={
            <Suspense fallback={<AdminFallback />}>
              <SeriesPage />
            </Suspense>
          }
        />
        <Route
          path="terminals"
          element={
            <Suspense fallback={<AdminFallback />}>
              <TerminalsPage />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <Suspense fallback={<AdminFallback />}>
              <UsersPage />
            </Suspense>
          }
        />
        <Route
          path="customers"
          element={
            <Suspense fallback={<AdminFallback />}>
              <CustomersPage />
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
